/**
 * Custom HorizontalBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { HorizontalBarChartComponent as ParentHorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { DataErrorComponent } from '@shared-lib/global-components/data-error/data-error.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { formatDecimalWithCommas } from '@shared-lib/utils/format.utils';

interface SalesOpportunityApiFilter {
  operator: string;
  attributeName: string;
  attributeValue: unknown;
  logicalOperator: string;
}

interface EquipmentUptimeRow {
  label: string;
  value: number;
  displayValue?: string;
}

@Component({
  selector: 'app-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrls: ['./horizontal-bar-chart.component.scss']
})
export class HorizontalBarChartComponent extends ParentHorizontalBarChartComponent {
  private customConfigService = inject(ConfigService);
  private globalFilterService = inject(GlobalFilterService);
  private resizeObserver?: ResizeObserver;
  private resizeRafId?: number;
  private observedChartDom?: HTMLElement | null;
  equipmentUptimeTitle = '';
  equipmentUptimeSubtitle = '';
  equipmentUptimeUnit = '';
  equipmentUptimeAxisLabel = '';
  equipmentUptimeRows: EquipmentUptimeRow[] = [];
  equipmentUptimeAverage: any;
  equipmentUptimeMetrics: Array<{ label: string; value: any; highlight?: string }> = [];
  // Constructor is inherited from parent to avoid dependency injection issues

  protected override loadAndRenderChart(filters?: any): any {
    if (this.isEquipmentUptimeLayout()) {
      this.loadEquipmentUptimeData(filters);
      return;
    }

    if (this.data?.chartConfig?.variant === 'sales-opportunity') {
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;

      if (this.observedChartDom && this.observedChartDom !== chartDom) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        this.observedChartDom = null;
        this.myChart?.dispose();
        this.myChart = undefined as any;
      }

      if (!this.myChart) {
        this.myChart = echarts.init(chartDom, 'dvm');
      }

      this.hasError = false;
      this.errorMessage = '';
      this.myChart.showLoading();

      this.customConfigService
        .getConfig(this.data.apiUrl, this.buildSalesOpportunityRequestConfig(filters), {
          ...filters,
          useQueryParams: this.data?.useQueryParams || false,
        })
        ?.subscribe({
          next: (res: any) => {
            if (!res || (Array.isArray(res) && res.length === 0)) {
              this.hasError = true;
              this.errorMessage = 'No data available for the selected filters.';
              this.myChart.clear();
              this.myChart.hideLoading();
              return;
            }

            this.hasError = false;
            this.renderChart(res);
            this.addResizeListener();
            this.scheduleChartResize();
            this.myChart.hideLoading();
          },
          error: () => {
            this.hasError = true;
            this.errorMessage = 'No Data available for the chart.';
            this.myChart.clear();
            this.myChart.hideLoading();
          },
        });

      return;
    }

    return super.loadAndRenderChart(filters);
  }

  protected override renderChart(chartData?: any): any {
    if (this.isSalesOpportunityChart(chartData)) {
      return this.renderSalesOpportunityChart(chartData);
    }

    return super.renderChart(chartData);
  }

  protected override setupCustomClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupCustomClickEvent();
  }

  protected override applyCategoryHighlight(categories?: any): any {
    // TODO: Add your custom logic here
    return super.applyCategoryHighlight(categories);
  }

  protected override resetCategoryHighlight(): any {
    // TODO: Add your custom logic here
    return super.resetCategoryHighlight();
  }

  protected override addResizeListener(): any {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) {
      return;
    }

    if (!this.resizeObserver && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleChartResize();
      });
    }

    if (this.resizeObserver && this.observedChartDom !== chartDom) {
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(chartDom);
      this.observedChartDom = chartDom;
    }

    return super.addResizeListener();
  }

  private isSalesOpportunityChart(chartData?: any): boolean {
    return this.data?.chartConfig?.variant === 'sales-opportunity' && !!chartData;
  }

  isEquipmentUptimeLayout(): boolean {
    return this.data?.chartConfig?.layout === 'equipment-uptime';
  }

  getEquipmentMaxValue(): number {
    const rowMax = Math.max(...this.equipmentUptimeRows.map((row) => Number(row.value) || 0), 0);
    const averageValue = Number(this.equipmentUptimeAverage?.value);
    return Math.max(rowMax, Number.isFinite(averageValue) ? averageValue : 0, 1);
  }

  getEquipmentBarWidth(value: any): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return 0;
    }

    return Math.min(100, (numericValue / this.getEquipmentMaxValue()) * 100);
  }

  getAveragePosition(): number {
    const numericValue = Number(this.equipmentUptimeAverage?.value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    return Math.min(100, Math.max(0, (numericValue / this.getEquipmentMaxValue()) * 100));
  }

  getRowDisplayValue(row: EquipmentUptimeRow): string {
    return row.displayValue ?? this.formatPercentValue(row.value);
  }

  getMetricClass(metric: any): string {
    const configuredHighlight = String(metric?.highlight || '').toLowerCase();
    if (configuredHighlight) {
      return configuredHighlight;
    }

    const numericValue = Number(metric?.value);
    if (!Number.isFinite(numericValue)) {
      return '';
    }

    return numericValue === 0 ? 'positive' : 'negative';
  }

  formatEquipmentMetric(metric: any): string {
    return metric?.displayValue ?? this.formatPercentValue(metric?.value);
  }

  private loadEquipmentUptimeData(filters?: any): void {
    this.hasError = false;
    this.errorMessage = '';

    this.customConfigService
      .getConfig(this.data.apiUrl, this.data, {
        ...filters,
        useQueryParams: this.data?.useQueryParams || false,
      }, this.data.method)
      ?.subscribe({
        next: (res: any) => {
          const source = this.resolveEquipmentUptimeSource(res);
          if (!source) {
            this.hasError = true;
            this.errorMessage = 'No data available for the selected filters.';
            return;
          }

          this.applyEquipmentUptimeData(source);
          this.hasError = this.equipmentUptimeRows.length === 0;
          this.errorMessage = this.hasError ? 'No data available for the selected filters.' : '';
          this.wrapper.afterRender();
        },
        error: () => {
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
        },
      });
  }

  private resolveEquipmentUptimeSource(response: any): any {
    const responsePath = this.data?.responsePath;
    if (responsePath) {
      const source = this.getByPath(response, responsePath);
      if (source) {
        return source;
      }
    }

    const mappedSource = this.mapEngineDowntimeResponse(response);
    if (mappedSource) {
      return mappedSource;
    }

    return (
      response?.responseList?.[0]?.data?.[0] ||
      response?.responseList?.[0]?.data ||
      response?.data?.[0] ||
      response?.data ||
      response
    );
  }

  private mapEngineDowntimeResponse(response: any): any | null {
    const source =
      response?.responseList?.[0]?.responseData?.[0] ||
      response?.responseData?.[0] ||
      null;

    if (!source || typeof source !== 'object' || !Array.isArray(source?.data)) {
      return null;
    }

    const rows = source.data
      .map((item: any) => {
        const value = Number(item?.unavailability_percentage);
        return {
          label: String(item?.month_year || item?.label || ''),
          value: Number.isFinite(value) ? value : 0,
          displayValue: this.formatPercentValue(value),
        };
      })
      .filter((row: EquipmentUptimeRow) => row.label);

    const averageValue = Number(source?.average_unavailability_percentage);
    const bestRow = rows.reduce((best: EquipmentUptimeRow | null, current: EquipmentUptimeRow) => {
      if (!best) return current;
      return current.value < best.value ? current : best;
    }, null);
    const worstRow = rows.reduce((worst: EquipmentUptimeRow | null, current: EquipmentUptimeRow) => {
      if (!worst) return current;
      return current.value > worst.value ? current : worst;
    }, null);
    const firstRow = source.data[0] || {};

    return {
      metadata: {
        chartTitle: 'Equipment Downtime',
        chartSubtitle: this.buildEquipmentUptimeSubtitle(firstRow, rows.length),
        unitBadge: '%',
        axisLabel: 'Downtime %',
        average: Number.isFinite(averageValue)
          ? {
              label: `Avg ${this.formatPercentValue(averageValue)}`,
              value: averageValue,
            }
          : null,
        footerMetrics: [
          ...(Number.isFinite(averageValue)
            ? [{
                label: 'Avg Downtime',
                value: averageValue,
                displayValue: this.formatPercentValue(averageValue),
              }]
            : []),
          ...(bestRow
            ? [{
                label: 'Best Month',
                value: bestRow.value,
                displayValue: `${bestRow.label}  ${bestRow.displayValue || this.formatPercentValue(bestRow.value)}`,
                highlight: 'positive',
              }]
            : []),
          ...(worstRow
            ? [{
                label: 'Worst Month',
                value: worstRow.value,
                displayValue: `${worstRow.label}  ${worstRow.displayValue || this.formatPercentValue(worstRow.value)}`,
                highlight: 'negative',
              }]
            : []),
        ],
      },
      rows,
    };
  }

  private applyEquipmentUptimeData(source: any): void {
    const chartConfig = this.data?.chartConfig || {};
    const metadata = source?.metadata || source?.chartMetadata || source || {};
    const rows = source?.rows || source?.data || source?.items || [];

    this.equipmentUptimeTitle = this.readFirst(metadata, chartConfig, ['title', 'heading', 'chartTitle']);
    this.equipmentUptimeSubtitle = this.readFirst(metadata, chartConfig, ['subtitle', 'subTitle', 'chartSubtitle']);
    this.equipmentUptimeUnit = this.readFirst(metadata, chartConfig, ['unitBadge', 'unit', 'uom']);
    this.equipmentUptimeAxisLabel = this.readFirst(metadata, chartConfig, ['axisLabel', 'xAxisLabel']);
    this.equipmentUptimeAverage = metadata?.average || metadata?.benchmark || source?.average || source?.benchmark;
    this.equipmentUptimeMetrics = Array.isArray(metadata?.footerMetrics || metadata?.metrics)
      ? (metadata.footerMetrics || metadata.metrics)
      : [];
    this.equipmentUptimeRows = Array.isArray(rows)
      ? rows
          .map((row: any) => {
            const value = Number(row?.value ?? row?.percentage ?? row?.downtime);
            return {
              label: String(row?.label || row?.month || row?.name || ''),
              value: Number.isFinite(value) ? value : 0,
              displayValue: row?.displayValue,
            };
          })
          .filter((row: EquipmentUptimeRow) => row.label)
      : [];
  }

  private formatPercentValue(value: any): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return String(value ?? '');
    }

    return `${numericValue.toFixed(numericValue === 0 ? 0 : 2)}%`;
  }

  private getByPath(source: any, path: string): any {
    return path.split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }

      if (/^\d+$/.test(key)) {
        return acc[Number(key)];
      }

      return acc[key];
    }, source);
  }

  private readFirst(primary: any, fallback: any, keys: string[], defaultValue: any = ''): any {
    for (const key of keys) {
      const value = primary?.[key] ?? fallback?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return defaultValue;
  }

  private buildEquipmentUptimeSubtitle(source: any, rowCount: number): string {
    const machineModel = String(source?.machine_model || '').trim();
    const customerName = String(source?.customer_name || '').trim();
    const monthsLabel = rowCount > 0 ? `Last ${rowCount} Months` : '';

    return [machineModel, customerName, monthsLabel].filter(Boolean).join(' - ');
  }

  private renderSalesOpportunityChart(chartData: any): void {
    const variantConfig = this.data?.chartConfig || {};
    const source = this.resolveSalesOpportunitySource(chartData);
    const category = this.resolveCategory(source, variantConfig);
    const currentSales = Math.abs(this.readNumericValue(source, variantConfig.currentSalesKey, 'currentSales'));
    const listPrice = this.readNumericValue(source, variantConfig.listPriceKey);
    const configuredOpportunity = this.readNumericValue(source, variantConfig.simulatedOpportunityKey, 'simulatedOpportunity');
    const simulatedOpportunity = Math.abs(configuredOpportunity || Math.max(listPrice - currentSales, 0));
    const configuredPercent = this.readNumericValue(source, variantConfig.simulatedOpportunityPercentKey, 'simulatedOpportunityPercent');
    const simulatedOpportunityPercent = configuredPercent || (currentSales ? Math.round((simulatedOpportunity / currentSales) * 100) : 0);
    const currentColor = variantConfig.currentSalesColor || '#005f8f';
    const simulatedColor = variantConfig.simulatedOpportunityColor || '#718d16';
    const currentLabel = variantConfig.currentSalesLabel || 'Current Sales';
    const simulatedLabel = variantConfig.simulatedOpportunityLabel || 'Simulated\nOpp. ($)';
    const yAxisName = variantConfig.yAxisName || category || 'Regional\nSales ($M)';
    const total = Math.max(currentSales + simulatedOpportunity, 1);

    this.allCategories = [category || yAxisName];

    const option = {
      backgroundColor: '#ffffff',
      grid: {
        left: variantConfig.gridLeft || 270,
        right: variantConfig.gridRight || 28,
        top: Math.max(variantConfig.gridTop || 30, 58),
        bottom: variantConfig.gridBottom || 24,
        containLabel: false,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: () => [
          `${currentLabel.replace('\n', ' ')}: ${this.formatChartNumber(currentSales)}`,
          `${simulatedLabel.replace('\n', ' ')}: ${this.formatChartNumber(simulatedOpportunity)}`,
        ].join('<br/>'),
      },
      legend: {
        show: true,
        top: 0,
        left: 'center',
        itemWidth: 18,
        itemHeight: 12,
        itemGap: 20,
        textStyle: {
          color: '#111111',
          fontSize: 16,
        },
        data: [currentLabel, simulatedLabel],
      },
      xAxis: {
        type: 'value',
        show: false,
        inverse: true,
        max: Math.ceil(total * 1.04),
      },
      yAxis: {
        type: 'category',
        data: [category || yAxisName],
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#222222',
            width: 2,
          },
        },
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: variantConfig.yAxisNameGap || 44,
        nameRotate: 90,
        nameTextStyle: {
          color: '#111111',
          fontSize: variantConfig.axisFontSize || 18,
          fontWeight: 400,
        },
      },
      series: [
        {
          name: currentLabel,
          type: 'bar',
          stack: 'sales',
          barWidth: variantConfig.barWidth || 86,
          data: [currentSales],
          itemStyle: { color: currentColor },
          label: {
            show: true,
            position: 'inside',
            color: '#ffffff',
            fontSize: variantConfig.valueFontSize || 20,
            formatter: ({ value }: any) => this.formatChartNumber(value),
          },
        },
        {
          name: simulatedLabel,
          type: 'bar',
          stack: 'sales',
          barWidth: variantConfig.barWidth || 86,
          data: [simulatedOpportunity],
          itemStyle: { color: simulatedColor },
          label: {
            show: true,
            position: 'inside',
            color: '#ffffff',
            fontSize: variantConfig.valueFontSize || 20,
            formatter: () => this.formatChartNumber(simulatedOpportunity),
          },
        },
      ],
    };

    this.myChart.setOption(option, true);
    this.scheduleChartResize();
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    this.setupCustomClickEvent();
  }

  override ngOnDestroy(): void {
    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = undefined;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.observedChartDom = null;
    super.ngOnDestroy();
  }

  private resolveSalesOpportunitySource(chartData: any): any {
    const dataKey = this.data?.dataKey;

    if (dataKey && chartData?.[dataKey]) {
      return Array.isArray(chartData[dataKey]) ? chartData[dataKey][0] || {} : chartData[dataKey];
    }

    if (Array.isArray(chartData)) {
      return chartData[0] || {};
    }

    if (Array.isArray(chartData?.responseList?.[0]?.data)) {
      return chartData.responseList[0].data[0] || {};
    }

    if (Array.isArray(chartData?.data)) {
      return chartData.data[0] || {};
    }

    if (chartData && typeof chartData === 'object') {
      const values = Object.values(chartData);
      if (values.length === 1 && values[0] && typeof values[0] === 'object' && !Array.isArray(values[0])) {
        return values[0];
      }
    }

    return chartData || {};
  }

  private buildSalesOpportunityRequestConfig(filters?: any): any {
    if (!this.data?.chartConfig?.requestBuilder?.enabled) {
      return this.data;
    }

    const requestBuilder = this.data.chartConfig.requestBuilder;
    const sliderFilterKey = requestBuilder.sliderFilterKey || 'pricing_lever_value';
    const requestParams = this.resolveRequestParams(filters);
    const sliderValue = this.resolveSliderValue(requestParams?.[sliderFilterKey], requestBuilder.defaultSliderValue ?? 2);
    const payload = this.cloneValue(this.data.customPayload || {});
    const filterList = this.buildFilterList(requestParams, sliderFilterKey, requestBuilder.filterAttributeMap);
    const attributeTemplates = Array.isArray(requestBuilder.attributesListTemplate)
      ? requestBuilder.attributesListTemplate
      : [];

    payload.entityName = requestBuilder.entityName || payload.entityName;
    payload.attributesList = attributeTemplates.map((template: string) =>
      String(template).replace(/\{\{\s*sliderValue\s*\}\}/g, String(sliderValue)),
    );
    payload.filterList = filterList;

    return {
      ...this.data,
      skipDefaultPayload: true,
      customPayload: payload,
    };
  }

  private resolveCategory(source: any, variantConfig: any): string {
    const categoryFields = Array.isArray(variantConfig.categoryFields) ? variantConfig.categoryFields : [];
    if (categoryFields.length > 0) {
      return categoryFields
        .map((field: string) => source?.[field])
        .filter((value: any) => value !== null && value !== undefined && value !== '')
        .join(' - ');
    }

    const categoryKey = variantConfig.categoryKey || 'category';
    return String(source?.[categoryKey] || source?.category || variantConfig.yAxisName || '');
  }

  private readNumericValue(source: any, key?: string, fallbackKey?: string): number {
    const value = key ? source?.[key] : undefined;
    const fallbackValue = fallbackKey ? source?.[fallbackKey] : undefined;
    return Number(value ?? fallbackValue ?? 0);
  }

  private buildFilterList(
    requestParams: Record<string, unknown> | undefined,
    sliderFilterKey: string,
    filterAttributeMap?: Record<string, string>,
  ): SalesOpportunityApiFilter[] {
    if (!requestParams || typeof requestParams !== 'object') {
      return [];
    }

    const filters = Object.entries(requestParams)
      .filter(([key]) => key !== sliderFilterKey)
      .map(([key, value]) => {
        const flattenedValues = (Array.isArray(value) ? value.flat(Infinity) : [value]).filter(
          (item) => item !== null && item !== undefined && item !== '',
        );

        if (!flattenedValues.length) {
          return null;
        }

        const attributeName = filterAttributeMap?.[key] || key;
        const isSingleValue = flattenedValues.length === 1;

        return {
          operator: isSingleValue ? '=' : 'IN',
          attributeName,
          attributeValue: isSingleValue ? flattenedValues[0] : flattenedValues,
          logicalOperator: 'AND',
        } satisfies SalesOpportunityApiFilter;
      })
      .filter((item): item is SalesOpportunityApiFilter => item !== null);

    if (filters.length) {
      filters[filters.length - 1].logicalOperator = '';
    }

    return filters;
  }

  private resolveSliderValue(rawValue: unknown, fallback: number): number {
    const flattenedValues = Array.isArray(rawValue) ? rawValue.flat(Infinity) : [rawValue];
    const parsed = Number(flattenedValues.find((value) => value !== null && value !== undefined && value !== ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private resolveRequestParams(filters?: any): Record<string, unknown> {
    const incomingRequestParams = filters?.requestParams;
    if (incomingRequestParams && typeof incomingRequestParams === 'object') {
      return incomingRequestParams;
    }

    const globalFilters = this.globalFilterService.getCurrentFilters?.();
    const globalRequestParams = globalFilters?.requestParams;
    if (globalRequestParams && typeof globalRequestParams === 'object') {
      return globalRequestParams;
    }

    return {};
  }

  private cloneValue<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map((item) => this.cloneValue(item)) as T;
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce((acc, [key, entryValue]) => {
        acc[key] = this.cloneValue(entryValue);
        return acc;
      }, {} as Record<string, unknown>) as T;
    }

    return value;
  }

  private formatChartNumber(value: number): string {
    return formatDecimalWithCommas(Math.round(Number(value) || 0));
  }

  private scheduleChartResize(): void {
    if (!this.myChart) {
      return;
    }

    if (this.resizeRafId) {
      cancelAnimationFrame(this.resizeRafId);
    }

    this.resizeRafId = requestAnimationFrame(() => {
      this.resizeRafId = requestAnimationFrame(() => {
        this.myChart?.resize();
      });
    });
  }
}
